import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  MessageSquare,
  Send,
  Bot,
  User,
  Target,
  Clock,
  FileVideo,
  X,
  Lightbulb,
  Zap,
  Scissors,
  Move,
  Volume2,
  Sparkles,
  Code,
  PlayCircle,
  History,
  Settings,
  TrendingUp,
  Brain,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Mic,
  MicOff,
  MoreVertical,
  Expand,
  Minimize,
  BookOpen,
  Star
} from 'lucide-react';
import aiChatService from '../services/aiChatService';
import timelineStateManager from '../services/timelineStateManager';
import smartCodeGenerator from '../services/smartCodeGenerator';

/**
 * Enhanced Chat Interface - Unified editing experience with advanced features
 * 
 * This component provides a comprehensive chat interface that integrates all
 * the AI-powered editing features: selected clips chat, smart suggestions,
 * conversation history, analytics, and context-aware assistance.
 */
const EnhancedChatInterface = ({ 
  selectedClips = [],
  selectionContext = null,
  timelineContext = {},
  onChatMessage,
  onCodeGenerated,
  onExecuteCode,
  isProcessing = false,
  className = ''
}) => {
  // Chat state
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('chat');
  
  // UI state
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Feature state
  const [suggestions, setSuggestions] = useState([]);
  const [voiceInput, setVoiceInput] = useState(false);
  const [contextualHelp, setContextualHelp] = useState(null);
  
  // Refs
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const voiceRecognition = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Focus input when component mounts
  useEffect(() => {
    if (!isExpanded) return;
    inputRef.current?.focus();
  }, [isExpanded]);

  // Load conversation history from state manager
  useEffect(() => {
    const contextHistory = timelineStateManager.getConversationContext({ lastN: 10 });
    if (contextHistory.length > 0) {
      setChatHistory(contextHistory.map(entry => ({
        id: entry.timestamp,
        role: entry.userMessage ? 'user' : 'assistant',
        content: entry.userMessage || entry.aiResponse,
        timestamp: new Date(entry.timestamp).toISOString(),
        metadata: entry.metadata || {},
        code: entry.code,
        smartGeneration: entry.smartGeneration
      })));
    }
  }, []);

  // Generate smart suggestions based on context
  useEffect(() => {
    const generateSuggestions = async () => {
      try {
        const smartSuggestions = smartCodeGenerator.generateSmartSuggestions(timelineContext, {
          maxSuggestions: 6,
          minConfidence: 0.5
        });
        
        const aiSuggestions = aiChatService.getSmartSuggestions(timelineContext);
        
        // Merge and deduplicate suggestions
        const allSuggestions = [...smartSuggestions, ...aiSuggestions];
        const uniqueSuggestions = allSuggestions.filter((suggestion, index, self) => 
          index === self.findIndex(s => s.text === suggestion.text)
        );
        
        setSuggestions(uniqueSuggestions.slice(0, 8));
      } catch (error) {
        console.error('Failed to generate suggestions:', error);
        setSuggestions([]);
      }
    };

    if (Object.keys(timelineContext).length > 0) {
      generateSuggestions();
    }
  }, [timelineContext, selectedClips]);

  // Generate contextual help based on current state
  const contextualHelpData = useMemo(() => {
    if (!timelineContext.clips || timelineContext.clips.length === 0) {
      return {
        title: "Getting Started",
        content: "Upload video clips to your timeline to begin editing with AI assistance.",
        type: "info"
      };
    }

    if (selectedClips.length > 1) {
      return {
        title: "Multi-Clip Selection",
        content: `You have ${selectedClips.length} clips selected. Try commands like "arrange by energy", "create montage", or "speed up 2x".`,
        type: "tip"
      };
    }

    if (timelineContext.duration > 180) {
      return {
        title: "Long Timeline",
        content: "Your timeline is quite long. Consider cutting to key moments or creating highlights for better engagement.",
        type: "suggestion"
      };
    }

    return {
      title: "AI Assistant Ready",
      content: "Ask me to edit your timeline using natural language. I can arrange clips, adjust timing, add effects, and more!",
      type: "ready"
    };
  }, [timelineContext, selectedClips]);

  // Handle sending chat message
  const handleSendMessage = useCallback(async () => {
    if (!message.trim() || isProcessing) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
      selectionContext: selectionContext
    };

    setChatHistory(prev => [...prev, userMessage]);
    setMessage('');

    try {
      // Determine if this is a selected clips operation
      const isSelectedClipsOperation = selectedClips.length > 0 && (
        /these|selected|current/i.test(message) ||
        selectionContext
      );

      let response;
      
      if (isSelectedClipsOperation) {
        // Use selected clips chat handler
        response = await onChatMessage?.(message, {
          selectedClips,
          selectionContext,
          chatHistory,
          mode: 'selected_clips'
        });
      } else {
        // Use general chat handler
        response = await onChatMessage?.(message, {
          timelineContext,
          chatHistory,
          mode: 'general'
        });
      }

      if (response) {
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: response.content || response.message,
          code: response.code,
          timestamp: new Date().toISOString(),
          metadata: response.metadata || {},
          intentAnalysis: response.intentAnalysis,
          queryResult: response.queryResult,
          smartGeneration: response.smartGeneration
        };

        setChatHistory(prev => [...prev, assistantMessage]);

        // If code was generated, notify parent
        if (response.code && onCodeGenerated) {
          onCodeGenerated(response.code, response);
        }
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}`,
        timestamp: new Date().toISOString(),
        error: true
      };
      setChatHistory(prev => [...prev, errorMessage]);
    }
  }, [message, isProcessing, selectedClips, selectionContext, timelineContext, chatHistory, onChatMessage, onCodeGenerated]);

  // Handle suggestion click
  const handleSuggestionClick = useCallback((suggestion) => {
    setMessage(suggestion.command);
    inputRef.current?.focus();
  }, []);

  // Toggle voice input
  const toggleVoiceInput = useCallback(() => {
    if (!voiceInput) {
      // Start voice recognition
      if ('webkitSpeechRecognition' in window) {
        const recognition = new window.webkitSpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          setMessage(transcript);
          setVoiceInput(false);
        };

        recognition.onerror = () => {
          setVoiceInput(false);
        };

        recognition.start();
        voiceRecognition.current = recognition;
        setVoiceInput(true);
      }
    } else {
      // Stop voice recognition
      if (voiceRecognition.current) {
        voiceRecognition.current.stop();
        voiceRecognition.current = null;
      }
      setVoiceInput(false);
    }
  }, [voiceInput]);

  // Get timeline analytics
  const timelineAnalytics = useMemo(() => {
    return timelineStateManager.getTimelineAnalytics?.() || {};
  }, [timelineContext]);

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isExpanded) {
    return (
      <div className={`bg-zinc-800 border border-zinc-600 rounded-lg ${className}`}>
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-4 h-4 text-blue-400" />
            <span className="font-medium text-white text-sm">AI Assistant</span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsExpanded(true)}
            className="text-gray-400 hover:text-white"
          >
            <Expand className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-zinc-800 border border-zinc-600 rounded-lg shadow-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-zinc-600">
        <div className="flex items-center space-x-2">
          <Brain className="w-4 h-4 text-blue-400" />
          <span className="font-medium text-white">Enhanced AI Assistant</span>
          {selectedClips.length > 0 && (
            <Badge variant="outline" className="text-xs">
              {selectedClips.length} selected
            </Badge>
          )}
        </div>
        <div className="flex items-center space-x-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="text-gray-400 hover:text-white"
          >
            <TrendingUp className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowHistory(!showHistory)}
            className="text-gray-400 hover:text-white"
          >
            <History className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsExpanded(false)}
            className="text-gray-400 hover:text-white"
          >
            <Minimize className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Contextual Help */}
      {contextualHelpData && (
        <div className="p-3 bg-zinc-900 border-b border-zinc-600">
          <div className="flex items-start space-x-2">
            <div className="mt-0.5">
              {contextualHelpData.type === 'info' && <BookOpen className="w-3 h-3 text-blue-400" />}
              {contextualHelpData.type === 'tip' && <Lightbulb className="w-3 h-3 text-yellow-400" />}
              {contextualHelpData.type === 'suggestion' && <Star className="w-3 h-3 text-green-400" />}
              {contextualHelpData.type === 'ready' && <Sparkles className="w-3 h-3 text-purple-400" />}
            </div>
            <div>
              <div className="text-xs font-medium text-white">{contextualHelpData.title}</div>
              <div className="text-xs text-gray-400 mt-1">{contextualHelpData.content}</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
        <TabsList className="grid w-full grid-cols-3 bg-zinc-900">
          <TabsTrigger value="chat" className="text-xs">
            <MessageSquare className="w-3 h-3 mr-1" />
            Chat
          </TabsTrigger>
          <TabsTrigger value="suggestions" className="text-xs">
            <Lightbulb className="w-3 h-3 mr-1" />
            Smart
          </TabsTrigger>
          <TabsTrigger value="analytics" className="text-xs">
            <TrendingUp className="w-3 h-3 mr-1" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Chat Tab */}
        <TabsContent value="chat" className="mt-0">
          <div className="flex flex-col h-80">
            {/* Chat Messages */}
            <ScrollArea className="flex-1 p-3">
              <div className="space-y-3">
                {chatHistory.length === 0 && (
                  <div className="text-center text-gray-400 text-sm py-4">
                    <Brain className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    <p>Ready to help with your video editing!</p>
                    <p className="text-xs mt-1">Try: "Create a cooking story", "Cut to 30 seconds", or "Speed up action clips"</p>
                  </div>
                )}
                
                {chatHistory.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs p-3 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : msg.error
                          ? 'bg-red-900 text-red-100'
                          : 'bg-zinc-700 text-gray-100'
                      }`}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        {msg.role === 'user' ? (
                          <User className="w-3 h-3" />
                        ) : (
                          <Bot className="w-3 h-3" />
                        )}
                        <span className="text-xs opacity-75">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm">{msg.content}</p>
                      
                      {/* Intent Analysis */}
                      {msg.intentAnalysis && (
                        <div className="mt-2 p-2 bg-zinc-800 rounded text-xs">
                          <div className="flex items-center space-x-2 mb-1">
                            <Target className="w-3 h-3 text-blue-400" />
                            <span className="text-blue-300">Intent</span>
                          </div>
                          <div className="text-gray-400">
                            {msg.intentAnalysis.primary_goal} • {msg.intentAnalysis.complexity}
                          </div>
                        </div>
                      )}

                      {/* Smart Generation Metadata */}
                      {msg.smartGeneration && (
                        <div className="mt-2 p-2 bg-zinc-800 rounded text-xs">
                          <div className="flex items-center space-x-2 mb-1">
                            <Sparkles className="w-3 h-3 text-purple-400" />
                            <span className="text-purple-300">Smart AI</span>
                          </div>
                          <div className="grid grid-cols-2 gap-1 text-gray-400">
                            <div>Strategy: {msg.smartGeneration.strategy}</div>
                            <div>Confidence: {Math.round(msg.smartGeneration.promptConfidence * 100)}%</div>
                          </div>
                        </div>
                      )}
                      
                      {msg.code && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onExecuteCode?.(msg.code)}
                          className="mt-2 text-xs"
                        >
                          <PlayCircle className="w-3 h-3 mr-1" />
                          Execute
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                
                {isProcessing && (
                  <div className="flex justify-start">
                    <div className="bg-zinc-700 text-gray-100 p-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Bot className="w-3 h-3" />
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        <span className="text-xs">AI is thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div ref={chatEndRef} />
            </ScrollArea>

            {/* Message Input */}
            <div className="p-3 border-t border-zinc-600">
              <div className="flex space-x-2">
                <Input
                  ref={inputRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={selectedClips.length > 0 ? "Tell me what to do with selected clips..." : "Describe your edit..."}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  disabled={isProcessing}
                  className="flex-1 bg-zinc-900 border-zinc-600 text-white text-sm"
                />
                <Button
                  onClick={toggleVoiceInput}
                  disabled={isProcessing}
                  size="sm"
                  variant={voiceInput ? "default" : "outline"}
                  className="px-2"
                >
                  {voiceInput ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={handleSendMessage}
                  disabled={isProcessing || !message.trim()}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Smart Suggestions Tab */}
        <TabsContent value="suggestions" className="mt-0">
          <div className="p-3 space-y-3 h-80 overflow-y-auto">
            <div className="text-sm font-medium text-white flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span>Smart Suggestions</span>
            </div>
            
            {suggestions.length === 0 ? (
              <div className="text-center text-gray-400 text-sm py-8">
                <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No suggestions available</p>
                <p className="text-xs mt-1">Add clips to your timeline to see smart suggestions</p>
              </div>
            ) : (
              <div className="grid gap-2">
                {suggestions.map((suggestion, index) => (
                  <Card
                    key={index}
                    className="p-3 bg-zinc-700 border-zinc-600 cursor-pointer hover:bg-zinc-600 transition-colors"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <div className="flex items-start space-x-2">
                      <div className="mt-1">
                        {suggestion.category === 'pattern' && <TrendingUp className="w-3 h-3 text-blue-400" />}
                        {suggestion.category === 'content' && <FileVideo className="w-3 h-3 text-green-400" />}
                        {suggestion.category === 'optimization' && <Zap className="w-3 h-3 text-yellow-400" />}
                        {suggestion.category === 'preference' && <Star className="w-3 h-3 text-purple-400" />}
                        {!suggestion.category && <Lightbulb className="w-3 h-3 text-gray-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-medium">{suggestion.text}</div>
                        <div className="text-gray-400 text-xs mt-1 truncate">{suggestion.command}</div>
                        {suggestion.confidence && (
                          <div className="flex items-center space-x-1 mt-1">
                            <div className="text-xs text-gray-500">Confidence:</div>
                            <div className="text-xs text-green-400">{Math.round(suggestion.confidence * 100)}%</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-0">
          <div className="p-3 space-y-3 h-80 overflow-y-auto">
            <div className="text-sm font-medium text-white flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span>Timeline Analytics</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <Card className="p-3 bg-zinc-700 border-zinc-600">
                <div className="text-xs text-gray-400">Total Clips</div>
                <div className="text-lg font-bold text-white">{timelineAnalytics.totalClips || 0}</div>
              </Card>
              <Card className="p-3 bg-zinc-700 border-zinc-600">
                <div className="text-xs text-gray-400">Duration</div>
                <div className="text-lg font-bold text-white">{formatTime(timelineAnalytics.totalDuration || 0)}</div>
              </Card>
              <Card className="p-3 bg-zinc-700 border-zinc-600">
                <div className="text-xs text-gray-400">Avg Clip</div>
                <div className="text-lg font-bold text-white">{formatTime(timelineAnalytics.averageClipDuration || 0)}</div>
              </Card>
              <Card className="p-3 bg-zinc-700 border-zinc-600">
                <div className="text-xs text-gray-400">Sessions</div>
                <div className="text-lg font-bold text-white">{timelineAnalytics.editingSessions || 0}</div>
              </Card>
            </div>

            {timelineAnalytics.mostUsedOperations?.length > 0 && (
              <div>
                <div className="text-sm text-gray-300 mb-2">Most Used Operations</div>
                <div className="space-y-1">
                  {timelineAnalytics.mostUsedOperations.map((op, index) => (
                    <div key={index} className="flex justify-between text-xs">
                      <span className="text-gray-400">{op.operation}</span>
                      <span className="text-white">{op.count}x</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {timelineAnalytics.contentTypes && Object.keys(timelineAnalytics.contentTypes).length > 0 && (
              <div>
                <div className="text-sm text-gray-300 mb-2">Content Distribution</div>
                <div className="space-y-1">
                  {Object.entries(timelineAnalytics.contentTypes).slice(0, 5).map(([type, count]) => (
                    <div key={type} className="flex justify-between text-xs">
                      <span className="text-gray-400">{type}</span>
                      <span className="text-white">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EnhancedChatInterface;